"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetWorkflowById from "@/hooks/requests/wokflow/use-get-workflow-by-id";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";

import DrawflowEditor from "./components/DrawflowEditor";

type WorkflowViewPageProps = {
  messages: WorkflowMessages;
};

export default function WorkflowViewPage({ messages }: WorkflowViewPageProps) {
  const { workflowId } = useParams();
  const { lang } = useLanguage();

  const {
    data: workflow,
    isLoading: isLoadingWorkflow,
    error,
  } = useGetWorkflowById({
    _id: Number(workflowId) || 0,
  });

  const {
    breadCrumbTitle,
    updateWorkflowPageTitle: pageTitle,
    workflowNotFoundError,
    workflowNotFoundAlt,
  } = messages;

  const BREAD_CRUMBS = [{ title: breadCrumbTitle, href: ROUTE_URLS.workflow(lang) }];
  const shouldLoadError = !workflow && !!error && !isLoadingWorkflow;

  const key = Array.isArray(workflowId) ? workflowId[0] : workflowId;
  const workflowKey = isNaN(Number(key)) ? key : Number(key);

  return (
    <PageLayout title={pageTitle} breadCrumbs={BREAD_CRUMBS} messages={messages}>
      <PageContainer>
        <Card sx={{ overflow: "hidden" }}>
          <Suspense isSuspended={!!isLoadingWorkflow} fallback={<CircularProgress size={50} />}>
            <DrawflowEditor key={workflowKey} workflow={workflow} messages={messages} />
            {!!shouldLoadError && (
              <AlertBlock
                title={workflowNotFoundError}
                description={error.message}
                alt={workflowNotFoundAlt}
              />
            )}
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
